import { Alert, Button, Table, Tag, Typography } from 'antd';
import React from 'react';
import { openBusinessDetail } from '@/components/BusinessDetailModal';
import PageSection from '@/components/PageSection';
import { useParkTenantContextKey } from '@/hooks/useParkTenantContext';
import { parkApi } from '@/services/bff/park.api';
import { businessTableColumns } from '@/utils/businessTableColumns';
import { formatMoney } from '../InvestmentOrders/shared';

type Order = Awaited<
  ReturnType<typeof parkApi.listEnterpriseLibraryInvestmentOrders>
>['items'][number];

export default function EnterpriseInvestmentOrders({
  parkEnterpriseUuid,
}: {
  parkEnterpriseUuid: string;
}) {
  const contextKey = useParkTenantContextKey();
  const [data, setData] = React.useState<{ items: Order[]; total: number }>({
    items: [],
    total: 0,
  });
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [version, refresh] = React.useReducer((n) => n + 1, 0);
  const [pagination, setPagination] = React.useState({
    pageNumber: 1,
    pageSize: 10,
  });
  const scopeKey = contextKey + ':' + parkEnterpriseUuid;
  const [pageScope, setPageScope] = React.useState(scopeKey);
  if (pageScope !== scopeKey) {
    setPageScope(scopeKey);
    setPagination({ pageNumber: 1, pageSize: 10 });
  }
  React.useEffect(() => {
    let active = true;
    setData({ items: [], total: 0 });
    setError('');
    setLoading(true);
    void parkApi
      .listEnterpriseLibraryInvestmentOrders(parkEnterpriseUuid, pagination)
      .then((result) => {
        if (active) setData(result);
      })
      .catch((e) => {
        if (active) setError(e.message || '关联订单加载失败');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [parkEnterpriseUuid, contextKey, version, pagination]);
  return (
    <PageSection
      title={`关联订单（${data.total}）`}
      extra={<Button onClick={refresh}>刷新</Button>}
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          title={error}
          action={<Button onClick={refresh}>重试</Button>}
        />
      ) : null}
      <Typography.Paragraph type="secondary">
        展示当前园区关联的全部正式订单；财务核销后同步显示已收金额。
      </Typography.Paragraph>
      <Table<Order>
        rowKey="uuid"
        loading={loading}
        dataSource={data.items}
        tableLayout="fixed"
        scroll={{ x: 950 }}
        pagination={{
          current: pagination.pageNumber,
          pageSize: pagination.pageSize,
          total: data.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 笔`,
          onChange: (pageNumber, pageSize) =>
            setPagination({
              pageNumber: pageSize === pagination.pageSize ? pageNumber : 1,
              pageSize,
            }),
        }}
        columns={businessTableColumns([
          {
            title: '订单编号',
            width: 210,
            dataIndex: 'orderNo',
            render: (value, row) => (
              <Button
                type="link"
                onClick={() =>
                  openBusinessDetail(`/attraction/order/${row.uuid}`)
                }
              >
                {value}
              </Button>
            ),
          },
          { title: '订单名称', dataIndex: 'title', ellipsis: true, width: 180 },
          {
            title: '合同金额',
            width: 140,
            dataIndex: 'contractAmount',
            align: 'right',
            render: formatMoney,
          },
          {
            title: '收款单',
            width: 110,
            render: (_, row) => (
              <Tag color={row.hasReceipt ? 'success' : 'default'}>
                {row.hasReceipt ? '已生成' : '未生成'}
              </Tag>
            ),
          },
          {
            title: '已收金额',
            width: 140,
            dataIndex: 'settledAmount',
            align: 'right',
            render: formatMoney,
          },
          {
            title: '来源合同',
            width: 120,
            render: (_, row) => (
              <Button
                type="link"
                disabled={!row.sourceContractUuid}
                onClick={() =>
                  openBusinessDetail(`/contract/${row.sourceContractUuid}`)
                }
              >
                查看合同
              </Button>
            ),
          },
        ])}
      />
    </PageSection>
  );
}
